import { Divider, Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch";
import { selectPivotSettingsState, selectValues } from "../../../../../store/selectors/fieldsStateSelectors";
import { fieldsStateActions } from "../../../../../store/thunks/fieldStatesThunks";
import CheckboxField from "../../../../common/CheckboxField.tsx";
import SwitchField from "../../../../common/SwitchField.tsx";
import BlankFieldSettings from "../../../common/BlankFieldSettings.tsx";

export default function Settings({ readonlyMode }: { readonlyMode: boolean }) {
  const settingsAreaValues = useSelector(selectPivotSettingsState);
  const dispatch = useAppDispatch();
  const values = useSelector(selectValues);
  const hideBlankRows = settingsAreaValues.hideBlankRows === true;
  const showGroupedColumns = settingsAreaValues.showGroupedColumns === true;

  return (
    <Stack spacing={1.5} sx={{ width: "100%" }}>
      <Stack spacing={1.5} sx={{ pb: 1.5 }}>
        <Typography variant="subtitle1">General</Typography>
        <Stack>
          <SwitchField
            label="Hide Blank Rows"
            checked={hideBlankRows}
            onChange={(hideBlankRows) => {
              const resetHideZeroRows = hideBlankRows === false ? false : settingsAreaValues.hideZeroRows;
              dispatch(
                fieldsStateActions.updateSettings({ settings: { hideBlankRows, hideZeroRows: resetHideZeroRows } })
              );
            }}
            tooltipText="Hides rows in which all measure values are empty."
            containerSx={{ py: 0.75 }}
            disabled={readonlyMode}
          />
          {hideBlankRows && (
            <CheckboxField
              checked={settingsAreaValues.hideZeroRows === true}
              onChange={(value) => dispatch(fieldsStateActions.updateSettings({ settings: { hideZeroRows: value } }))}
              label="Treat Zeros as Empty"
              formControlLabelSx={{ ml: 1.25 }}
              disabled={readonlyMode}
            />
          )}
          <BlankFieldSettings
            fields={values}
            reportType="Pivot"
            settings={{
              hideBlank: settingsAreaValues.hideBlankColumns,
              hideZero: settingsAreaValues.hideZeroColumns,
              scope: settingsAreaValues.hideBlankColumnsScope,
              fieldIds: settingsAreaValues.hideBlankColumnIds,
            }}
            readonlyMode={readonlyMode}
            onChange={({ hideBlank, hideZero, scope, fieldIds }) =>
              dispatch(
                fieldsStateActions.updateSettings({
                  settings: {
                    hideBlankColumns: hideBlank,
                    hideZeroColumns: hideZero,
                    hideBlankColumnsScope: scope,
                    hideBlankColumnIds: fieldIds,
                  },
                })
              )
            }
          />
          <SwitchField
            label="Show Rows in Separate Columns"
            checked={showGroupedColumns}
            onChange={(value) => {
              const resetHideGroupRows = value === false ? false : settingsAreaValues.hideGroupRows;
              dispatch(
                fieldsStateActions.updateSettings({
                  settings: { showGroupedColumns: value, hideGroupRows: resetHideGroupRows },
                })
              );
            }}
            containerSx={{ py: 0.75 }}
            disabled={readonlyMode}
          />
          {showGroupedColumns && (
            <CheckboxField
              checked={settingsAreaValues.hideGroupRows === true}
              onChange={(value) => dispatch(fieldsStateActions.updateSettings({ settings: { hideGroupRows: value } }))}
              label="Hide Group Rows"
              tooltipText="Hides rows that have other rows under them, including their totals. Each remaining row keeps its group values in their columns."
              formControlLabelSx={{ ml: 1.25 }}
              disabled={readonlyMode}
            />
          )}
        </Stack>
        <Divider sx={{ borderStyle: "dashed" }} />
        <Typography variant="subtitle1">Totals</Typography>
        <SwitchField
          label="Show Rows Grand Total"
          checked={settingsAreaValues.rowsGrandTotal === true}
          onChange={(newValue) =>
            dispatch(fieldsStateActions.updateSettings({ settings: { rowsGrandTotal: newValue } }))
          }
          disabled={readonlyMode}
        />
      </Stack>
    </Stack>
  );
}
